import { isAxiosError } from "axios";
import api, { ApiResponse } from "./api";

export type AccountStatus = "active" | "disabled";
export type MembershipStatus = "active" | "archived" | "unassigned";

export interface SetupEmailDelivery {
  status: "sent" | "failed";
  retryable: boolean;
}

export interface PropertyManagerSummary {
  id: number;
  name: string;
  email: string;
  role: "property_manager";
  account_status: AccountStatus;
  image_url: string | null;
}

export interface BuildingMembership {
  building_id: number;
  status: "active";
  assigned_at: string;
  archived_at: null;
}

export interface BuildingManager extends PropertyManagerSummary {
  membership: BuildingMembership;
}

export interface PropertyManagerCandidate extends PropertyManagerSummary {
  membership_status: MembershipStatus;
}

export interface PropertyManagerListResult {
  data: BuildingManager[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface PropertyManagerSearchResult {
  matches: PropertyManagerCandidate[];
  exact_email_conflict: boolean;
}

export type AssignPropertyManagerRequest =
  | { mode: "existing"; user_id: number }
  | { mode: "create_or_attach"; name: string; email: string };

export interface PropertyManagerMutationResult {
  outcome:
    | "created_and_attached"
    | "attached_existing"
    | "reactivated"
    | "already_attached";
  manager: PropertyManagerSummary;
  membership: BuildingMembership;
  setup_email: SetupEmailDelivery | null;
}

export interface SetupLinkResult {
  setup_email: SetupEmailDelivery;
}

interface ApiErrorResponse {
  CODE: number;
  MESSAGE: string;
  RESULT: null;
  ERROR_CODE?: string;
  ERRORS?: Record<string, string[]>;
}

export class PropertyManagerApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly errors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = "PropertyManagerApiError";
  }
}

function assertSuccess<T>(
  status: number,
  body: ApiResponse<T> | ApiErrorResponse,
  fallback: string,
): T {
  if (status >= 200 && status < 300 && body.CODE >= 200 && body.CODE < 300) {
    return body.RESULT as T;
  }

  const problem = body as ApiErrorResponse;
  throw new PropertyManagerApiError(
    problem.MESSAGE || fallback,
    status,
    problem.ERROR_CODE,
    problem.ERRORS,
  );
}

function normalizeRequestError(error: unknown, fallback: string): never {
  if (isAxiosError<ApiErrorResponse>(error) && error.response) {
    const problem = error.response.data;
    throw new PropertyManagerApiError(
      problem?.MESSAGE || fallback,
      error.response.status,
      problem?.ERROR_CODE,
      problem?.ERRORS,
    );
  }

  if (error instanceof Error) {
    throw error;
  }

  throw new PropertyManagerApiError(fallback, 0);
}

export const buildingManagerService = {
  async listForBuilding(
    buildingId: number,
    signal?: AbortSignal,
  ): Promise<PropertyManagerListResult> {
    const response = await api.get<ApiResponse<PropertyManagerListResult>>(
      `/buildings/${buildingId}/property-managers`,
      { params: { page: 1, per_page: 100 }, signal },
    );
    return assertSuccess(response.status, response.data, "Failed to load property managers");
  },

  async searchCandidates(
    buildingId: number,
    query: string,
    signal?: AbortSignal,
  ): Promise<PropertyManagerSearchResult> {
    const response = await api.get<ApiResponse<PropertyManagerSearchResult>>(
      "/property-managers/search",
      { params: { query: query.trim(), building_id: buildingId, limit: 10 }, signal },
    );
    return assertSuccess(response.status, response.data, "Failed to search property managers");
  },

  async assign(
    buildingId: number,
    request: AssignPropertyManagerRequest,
  ): Promise<PropertyManagerMutationResult> {
    const response = await api.post<
      ApiResponse<PropertyManagerMutationResult> | ApiErrorResponse
    >(`/buildings/${buildingId}/property-managers`, request);
    return assertSuccess(response.status, response.data, "Failed to add property manager");
  },

  async attachExisting(
    buildingId: number,
    userId: number,
  ): Promise<PropertyManagerMutationResult> {
    return this.assign(buildingId, { mode: "existing", user_id: userId });
  },

  async createAndAttach(
    buildingId: number,
    input: { name: string; email: string },
  ): Promise<PropertyManagerMutationResult> {
    return this.assign(buildingId, {
      mode: "create_or_attach",
      name: input.name.trim(),
      email: input.email.trim(),
    });
  },

  async sendSetupLink(buildingId: number, userId: number): Promise<SetupLinkResult> {
    try {
      const response = await api.post<ApiResponse<SetupLinkResult> | ApiErrorResponse>(
        `/buildings/${buildingId}/property-managers/${userId}/setup-link`,
      );
      return assertSuccess(response.status, response.data, "Failed to send setup link");
    } catch (error) {
      return normalizeRequestError(error, "Failed to send setup link");
    }
  },

  async removeFromBuilding(buildingId: number, userId: number): Promise<void> {
    const response = await api.delete<ApiResponse<unknown> | ApiErrorResponse>(
      `/buildings/${buildingId}/property-managers/${userId}`,
    );
    if (response.status === 204) return;
    assertSuccess(response.status, response.data, "Failed to remove property manager");
  },
};

export default buildingManagerService;
